import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { Container } from '@plone/components';

import { getBreadcrumbs } from '@plone/volto/actions/breadcrumbs/breadcrumbs';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import { isHCoreLanding } from '../../../../../helpers/isHCoreLanding';

type Crumb = { title: string; url: string };
type RootState = { breadcrumbs?: { items?: Crumb[]; root?: string } };

const messages = defineMessages({
  breadcrumbs: { id: 'Breadcrumbs', defaultMessage: 'Breadcrumbs' },
});

const Breadcrumbs: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const hCoreLanding = isHCoreLanding(pathname);

  const items = useSelector((s: RootState) => s.breadcrumbs?.items || []);
  const root = useSelector((s: RootState) => s.breadcrumbs?.root || '/');

  useEffect(() => {
    if (hCoreLanding) return;

    const base = getBaseUrl(pathname);
    if (!hasApiExpander('breadcrumbs', base)) {
      dispatch(getBreadcrumbs(base) as any);
    }
  }, [dispatch, hCoreLanding, pathname]);

  const crumbs: Crumb[] = [{ title: 'Home', url: root }, ...items];

  if (hCoreLanding) return null;

  return (
    <div
      role="navigation"
      aria-label={intl.formatMessage(messages.breadcrumbs)}
      className="breadcrumbs"
    >
      <Container layout>
        <ol
          className="breadcrumb fcmh-breadcrumbs"
          role="list"
          aria-label="Breadcrumb"
        >
          {crumbs.map((item, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <React.Fragment key={`${item.url}-${index}`}>
                {index > 0 && (
                  <li className="divider" aria-hidden="true">
                    /
                  </li>
                )}
                <li
                  className={`section${isLast ? ' active' : ''}`}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {isLast ? (
                    <span>{item.title}</span>
                  ) : (
                    <Link to={item.url}>{item.title}</Link>
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ol>
      </Container>
    </div>
  );
};

export default Breadcrumbs;
